import React from 'react';

import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton as MuiIconButton,
  Stack,
  styled,
  Typography
} from '@mui/material';
import _ from 'lodash';
import { useDialogControl } from 'modules';
import { LoadingOverlay } from 'modules/layer0';

import * as dataService from './dataService';



const TitleBox = styled(Box)({
  width: 'fit-content',
  height: 50,
  display: 'flex',
  alignItems: 'center',
  paddingLeft: 24,
  paddingRight: 48,
  marginTop: 14,
  marginBottom: 14,
  background: 'linear-gradient(91.89deg, rgba(82, 136, 152, 0.6) -128.67%, #528898 112.89%)',
  clipPath: 'polygon(calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 0% 100%, 0 50%, 0% 0%);'
});

const TitleText = styled(Typography)({
  fontWeight: 600,
  color: '#FFFFFF',
  lineHeight: 2
});

const DialogHeaderBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between'
});

const IconButton = styled(MuiIconButton)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginRight: theme.spacing(1),
  alignSelf: 'self-start'
}));



export interface IProps {
  title: string;
}

export const FC: React.FC<IProps> = props => {
  const control = useDialogControl({});
  const { fnRunQuery, loading, data } = dataService.usePitchDeckTopicDetail();

  return (
    <>
      <LoadingOverlay active={loading} />

      <Button
        variant="contained"
        color="primary"
        style={{ width: 'fit-content' }}
        disabled={loading}
        onClick={async () => {
          await fnRunQuery(props.title);
          control.openDialog();
        }}>
        READ MORE
      </Button>

      <Dialog
        open={control.isOpen}
        onClose={control.closeDialog}
        maxWidth='lg'>
        {!loading && (
          <>
            <DialogHeaderBox>
              <TitleBox>
                <TitleText variant="h2">{data?.title}</TitleText>
              </TitleBox>

              <IconButton
                disabled={loading}
                onClick={control.closeDialog}>
                <CloseIcon color="error"/>
              </IconButton>
            </DialogHeaderBox>

            <DialogContent>
              <Stack spacing={4}>
                {__renderSection('Description', data?.description)}
                {__renderSection('Why is this important?', data?.whyIsThisImportant)}
                <Stack direction="row" spacing={8}>
                  {__renderSectionWithBulletPoint('What you’ll need', data?.whatYouWillNeed)}
                  {__renderSectionWithBulletPoint('Potential pitfalls', data?.potentialPitfalls)}
                  {__renderSectionWithBulletPoint('Common Successes', data?.commonSuccesses)}
                </Stack>
                {__renderSection('Closing Thoughts', data?.closingThoughts)}
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};



function __renderSection(title: string, content?: string) {
  return (
    <Stack spacing={2}>
      <Typography variant="h3">{title}</Typography>
      <Typography fontSize={18}>{content ?? 'None'}</Typography>
    </Stack>
  );
}



function __renderSectionWithBulletPoint(title: string, content?: string[]) {
  return (
    <Stack spacing={2} width="calc(100% / 3)">
      <Typography variant="h3">{title}</Typography>
      {content && (
        <ul style={{ color: '#486C82', paddingInlineStart: 16 }}>
          {_.map(content, (contentSegment, index) => (
            <li
              key={index}
              style={{ listStylePosition: 'outside' }}>
              {contentSegment}
            </li>
          ))}
        </ul>
      )}
    </Stack>
  );
}
